import { useMemo } from 'react';
import { Option } from '@common-fe/common-fe';
import _ from 'lodash';

import { suggestedDayCSS } from '@/modules/transaction/components/ReimburseMe/ClaimInfo/ClaimInfo.styles';
import theme from '@/styles/theme';
import { compactGroups } from '@/utils';

import { EligibleExpense, ParsedFileByOcr, RecommendedValue } from '../../FileScan/fileScan.types';

import useFilteredExpensesOptions from './useFilteredExpensesOptions';
import useSetMaxAmountExpenseOption from './useSetMaxAmountExpenseOption';

const getOptions = (options?: RecommendedValue[]) => (options?.map((option) => ({
  key: `${option.value}`,
  value: `${option.value}`,
  title: `${option.value}`,
  confidence: option.confidence,
})));

const covertToRecommendedValue = (key: keyof EligibleExpense, options?: EligibleExpense[]) => (
  options?.map((option) => ({
    key: `${option[key]}`,
    value: `${option[key]}`,
    title: `${option[key]}`,
  }))
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortByConfidence = (options: any[]) => ([...options]
  ?.sort((prev, next) => {
    if (prev?.isGroupTitle) return 0;
    if (next?.isGroupTitle) return 0;
    return _.toNumber(prev?.confidence || 0) > _.toNumber(next?.confidence || 0) ? -1 : 1;
  })
);

interface Props {
  providerOptions: Option[];
  servicesOptions: Option[];
  patientsOptions: Option[];
  scannedDocument?: ParsedFileByOcr;
  serviceDate?: string;
}

export const useClaimInfoGroups = ({
  providerOptions,
  servicesOptions,
  patientsOptions,
  scannedDocument,
  serviceDate,
}: Props) => {
  const hasEligibleExpenses = (scannedDocument?.expenses || []).length >= 2;
  const eligibleExpenses = useMemo(() => hasEligibleExpenses ? scannedDocument?.eligibleExpenseServices : [],
    [hasEligibleExpenses, scannedDocument]);
  const { sortedExpensesOptions, filteredExpense } = useFilteredExpensesOptions({ eligibleExpenses });

  useSetMaxAmountExpenseOption({
    filteredExpense,
    patientsOptions,
    servicesOptions,
  });

  const suggestedProviderOptions = useMemo(
    () => getOptions(scannedDocument?.facilityNames),
    [scannedDocument],
  );

  const suggestedPatientsOptions = useMemo(
    () => hasEligibleExpenses
      ? covertToRecommendedValue('patient', sortedExpensesOptions)
      : getOptions(scannedDocument?.patients),
    [hasEligibleExpenses, sortedExpensesOptions, scannedDocument],
  );

  const suggestedAmountOptions = useMemo(() => {
    if (hasEligibleExpenses) {
      const filteredAmounts = _(eligibleExpenses)
        .groupBy('patient')
        .map(group => _.maxBy(group, item => parseFloat(item?.amount || '')))
        .compact()
        .value();

      return covertToRecommendedValue('amount', filteredAmounts);
    }

    return getOptions(scannedDocument?.amounts)?.slice(0, 3);
  }, [hasEligibleExpenses, scannedDocument, eligibleExpenses]);

  const suggestedServicesOptions = useMemo(
    () => hasEligibleExpenses
      ? covertToRecommendedValue('service', sortedExpensesOptions)
      : getOptions(scannedDocument?.serviceDescriptions)?.sort((a, b) => (a?.value > b?.value ? 1 : -1)) ,
    [hasEligibleExpenses, sortedExpensesOptions, scannedDocument],
  );

  const serviceDatesGroups = useMemo(() => {
    if (hasEligibleExpenses) {
      const eligibleServiceDates = sortedExpensesOptions?.map((eligibleExpense) => eligibleExpense?.serviceDates)?.flat();

      return eligibleServiceDates?.map((eligibleServiceDate) => ({
        date: eligibleServiceDate,
        labelStyle: { color: theme.colors.success },
        dayCSS: suggestedDayCSS,
      }));
    }

    const serviceDatesOptions = scannedDocument?.serviceDates?.map((date) => ({
      date: date?.value,
      labelStyle: { color: theme.colors.success },
      dayCSS: suggestedDayCSS,
      confidence: date?.confidence,
    })) || [];

    return sortByConfidence(serviceDatesOptions);
  }, [hasEligibleExpenses, sortedExpensesOptions, scannedDocument]);

  const providerGroups = useMemo(() => sortByConfidence(compactGroups({
    suggestedGroup: {
      title: 'Choose one of the suggested options or enter your own',
      placeholderTitle: 'There are no suggested options. Please choose one from the list of others or enter your own',
      options: suggestedProviderOptions,
    },
    mainGroup: {
      title: 'Other',
      placeholderTitle: 'There are no other options',
      options: providerOptions,
    },
  })), [suggestedProviderOptions, providerOptions]);

  const patientGroups = useMemo(() => sortByConfidence(compactGroups({
    suggestedGroup: {
      title: 'Choose one of the suggested options or other options or add a new one using the [+] button',
      placeholderTitle: 'There are no suggested options. Please choose the most appropriate from the list of others or add a new one using the [+] button',
      options: suggestedPatientsOptions,
    },
    mainGroup: {
      title: 'Other',
      placeholderTitle: 'There are no other options',
      options: patientsOptions,
    },
  })), [suggestedPatientsOptions, patientsOptions]);

  const amountGroups = useMemo(() => sortByConfidence(compactGroups({
    mainGroup: {
      title: 'Choose one of the suggested options or enter your own',
      placeholderTitle: 'There are no suggested options. Please enter your own',
      options: suggestedAmountOptions,
    },
  })), [suggestedAmountOptions]);
  
  const serviceGroups = useMemo(() => serviceDate ? sortByConfidence(compactGroups({
    suggestedGroup: {
      title: 'Suggested options',
      placeholderTitle: 'There are no suggested options. Please select the most appropriate from the list of other services',
      options: suggestedServicesOptions,
    },
    mainGroup: {
      title: 'Other services covered by your plans',
      placeholderTitle: 'There are no other options',
      options: servicesOptions,
    },
  })) : [], [suggestedServicesOptions, servicesOptions, serviceDate]);

  return {
    providerGroups,
    patientGroups,
    serviceDatesGroups,
    serviceGroups,
    amountGroups,
    suggestedAmountOptions,
  };
};
